import React, { useEffect, useRef, useState } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

// 🔊 Built-in Audio Synthesizer (No external file needed)
function playPunchChime(type: 'success' | 'error') {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'success') {
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.setValueAtTime(880.00, ctx.currentTime + 0.1); // A5
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.45);
    } else {
      osc.frequency.setValueAtTime(220.00, ctx.currentTime); // A3
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch {}
}

export default function RemoteAttendancePage() {
  const [sessionId] = useState(() => new URLSearchParams(window.location.search).get('session') || '');
  const [status, setStatus] = useState<'loading' | 'ready' | 'punching' | 'success' | 'error'>('loading');
  const [result, setResult] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const isPunchingRef = useRef(false);

  // 1. Keep Phone Screen Permanently Awake (WakeLock)
  useEffect(() => {
    let wakeLock: any = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
        }
      } catch {}
    };
    requestWakeLock();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    });
    return () => {
      if (wakeLock) wakeLock.release().catch(() => {});
    };
  }, []);

  // 2. Validate Session on Load
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMsg('Invalid or missing attendance QR session.');
      return;
    }

    fetch(`${BASE}/employees/attendance/remote-punch-session/${sessionId}`)
      .then(async r => {
        if (!r.ok) throw new Error('Attendance session has expired. Please scan the QR code on desktop again.');
        return r.json();
      })
      .then(d => {
        setStatus('ready');
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(err.message || 'Session expired');
      });
  }, [sessionId]);

  // 3. Fingerprint Touch Handler (with Auto-Reset Loop)
  const handleTouchSensor = async () => {
    if (isPunchingRef.current) return;
    isPunchingRef.current = true;
    setStatus('punching');
    setErrorMsg('');

    try {
      if (!window.PublicKeyCredential) {
        throw new Error('Biometric authentication is not supported on this phone browser.');
      }

      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      let assertion: PublicKeyCredential | null = null;
      try {
        assertion = await navigator.credentials.get({
          publicKey: {
            challenge,
            timeout: 60000,
            userVerification: 'required',
          },
        }) as PublicKeyCredential;
      } catch (err: any) {
        throw new Error('No enrolled fingerprint found or scan was cancelled.');
      }

      if (!assertion) {
        throw new Error('Fingerprint scan was cancelled.');
      }

      const rawId = btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)));

      const res = await fetch(`${BASE}/employees/attendance/remote-punch-session/${sessionId}/punch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential_id: rawId }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Fingerprint not registered in system.');
      }

      // ✅ PUNCH SUCCESS!
      playPunchChime('success');
      setResult(data);
      setStatus('success');

      // ⚡ 2.5-Second Delay, then AUTO-RESET for the next person in line!
      setTimeout(() => {
        setResult(null);
        setStatus('ready');
        isPunchingRef.current = false;
      }, 2500);

    } catch (e: any) {
      playPunchChime('error');
      setStatus('error');
      setErrorMsg(e.message || 'Fingerprint verification failed.');
      isPunchingRef.current = false;

      // Auto-recover back to ready state after 2 seconds
      setTimeout(() => {
        setStatus('ready');
      }, 2000);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#070b14', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
      fontFamily: "'DM Sans', system-ui, sans-serif", color: '#fff',
      userSelect: 'none',
    }}>
      <div style={{
        background: '#0f172a', borderRadius: 28, padding: 32,
        width: '100%', maxWidth: 380, textAlign: 'center',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>
        
        {/* Loading State */}
        {status === 'loading' && (
          <div style={{ padding: 40, color: '#94a3b8', fontSize: 14 }}>Connecting to attendance scanner...</div>
        )}

        {/* ❌ Error State (Auto-recovers) */}
        {status === 'error' && (
          <div>
            <div style={{ fontSize: 56, marginBottom: 12 }}>❌</div>
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 8, color: '#f87171' }}>Verification Failed</h2>
            <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5, marginBottom: 20 }}>{errorMsg}</p>
            <button
              onClick={() => setStatus('ready')}
              style={{
                width: '100%', padding: '14px', background: '#1e293b', color: '#fff',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: 'pointer',
              }}
            >
              🔄 Try Again
            </button>
          </div>
        )}

        {/* 👆 Ready State (Queue Waiting) */}
        {status === 'ready' && (
          <div>
            <div
              onClick={handleTouchSensor}
              style={{
                width: 90, height: 90, borderRadius: '50%', background: '#1e293b',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 44, margin: '0 auto 16px', border: '2px solid #10b981',
                boxShadow: '0 0 24px rgba(16,185,129,0.25)', cursor: 'pointer',
              }}
            >
              👆
            </div>

            <h2 style={{ fontSize: 21, fontWeight: 900, marginBottom: 4 }}>Touch to Punch</h2>
            <p style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 24, lineHeight: 1.5 }}>
              Staff & Members: Place finger on sensor to mark attendance.
            </p>

            <button
              onClick={handleTouchSensor}
              style={{
                width: '100%', padding: '16px', borderRadius: 14,
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff', border: 'none', fontSize: 15, fontWeight: 800,
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(16,185,129,0.35)',
              }}
            >
              👆 Touch Fingerprint Sensor
            </button>
          </div>
        )}

        {/* Punching State */}
        {status === 'punching' && (
          <div>
            <div style={{ fontSize: 56, marginBottom: 12, animation: 'pulse 1.5s infinite' }}>⏳</div>
            <h2 style={{ fontSize: 18, fontWeight: 900, marginBottom: 8 }}>Verifying Sensor...</h2>
            <p style={{ fontSize: 13, color: '#94a3b8' }}>Recording attendance on server...</p>
          </div>
        )}

        {/* ✅ Success State (Auto-Resets in 2.5 seconds) */}
        {status === 'success' && result && (
          <div style={{ animation: 'fadeIn 0.15s ease' }}>
            <div style={{ fontSize: 64, marginBottom: 8 }}>
              {result.person_type === 'member'
                ? (result.module === 'gym' ? '🏋️' : '🍽️')
                : (result.scan_type === 'IN' ? '✅' : '🔴')}
            </div>

            <h2 style={{
              fontSize: 22, fontWeight: 900,
              color: result.scan_type === 'IN' ? '#34d399' : '#f87171',
              marginBottom: 4,
            }}>
              {result.person_type === 'member'
                ? (result.module === 'gym' ? 'Workout Check-In!' : 'Meal Punched!')
                : `Checked ${result.scan_type}!`}
            </h2>

            <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', marginTop: 6 }}>{result.name}</div>
            
            {result.plan_name && (
              <div style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa', marginTop: 4 }}>
                {result.plan_name}
              </div>
            )}

            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
              Timestamp: {result.time} · {result.date}
            </div>

            <div style={{
              marginTop: 20, padding: '8px 12px', background: 'rgba(255,255,255,0.06)',
              borderRadius: 10, fontSize: 11.5, color: '#6ee7b7', fontWeight: 700,
            }}>
              ⏳ Next person can punch in 2 seconds...
            </div>
          </div>
        )}

      </div>
    </div>
  );
}